import React, { useState } from 'react';
import { registerMember } from '../services/api';
import { getUTM } from '../utils/utm';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

const compressImage = (file, maxWidth = 1024, quality = 0.7) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, maxWidth / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

function MembershipPage() {
  const [formData, setFormData] = useState({
    name: '', civilIdNo: '', gender: '', mobileNo: '',
    email: '', bloodGroup: '', nativePlace: '', photo: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);
    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) data.append(key, formData[key]);
    });
    const utmSource = getUTM();
    if (utmSource) data.append('utmSource', utmSource);
    try {
      await registerMember(data);
      setSubmitted(true);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <img src="/learningcenter/membership_banner.png" alt="MISK Banner" style={styles.banner} />
          <div style={styles.successCard}>
            <h2 style={{color: '#155724', marginBottom: '10px'}}>Registration Successful!</h2>
            <p>Your membership information has been submitted successfully.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <img src="/learningcenter/membership_banner.png" alt="MISK Banner" style={styles.banner} />

        <div style={styles.card}>
          <h1 style={styles.title}>MISK Membership Update Form</h1>
          <hr style={styles.hr} />
          <p style={styles.desc}>
            <em>Please fill out the form below with your most up-to-date information for the MISK membership records.</em>
          </p>
          <ol style={styles.rules}>
            <li><strong>Age 18 & above registration will be considered</strong></li>
            <li><strong>Fields marked with * are mandatory</strong></li>
          </ol>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.card}>
            <label style={styles.label}>Name (as per Civil ID) <span style={styles.req}>*</span></label>
            <input style={styles.input} name="name" required value={formData.name} onChange={handleChange} placeholder="Short answer text" />
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Civil ID No. <span style={styles.req}>*</span></label>
            <input style={styles.input} name="civilIdNo" required value={formData.civilIdNo}
              onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 12); setFormData({...formData, civilIdNo: v}); }}
              placeholder="12 digit Civil ID number" inputMode="numeric" maxLength={12} pattern="\d{12}" title="Civil ID must be exactly 12 digits" />
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Gender <span style={styles.req}>*</span></label>
            <div style={{display: 'flex', gap: '30px'}}>
              {['Male', 'Female'].map(g => (
                <label key={g} style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px'}}>
                  <input type="radio" name="gender" value={g} required checked={formData.gender === g} onChange={handleChange} style={{width: '18px', height: '18px'}} />
                  {g}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Mobile No. <span style={styles.req}>*</span></label>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span style={{fontSize: '14px', fontWeight: 'bold', color: '#333'}}>🇰🇼 +965</span>
              <input style={{...styles.input, flex: 1}} name="mobileNo" required value={formData.mobileNo}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 8); setFormData({...formData, mobileNo: v}); }}
                placeholder="8 digit mobile number" inputMode="numeric" maxLength={8} pattern="\d{8}" title="Mobile number must be exactly 8 digits" />
            </div>
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Email ID</label>
            <input style={styles.input} name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Short answer text" />
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Blood Group <span style={styles.req}>*</span></label>
            <select name="bloodGroup" required value={formData.bloodGroup} onChange={handleChange} style={{...styles.input, borderBottom: '1px solid #ddd', cursor: 'pointer'}}>
              <option value="">Select Blood Group</option>
              {bloodGroups.map(bg => <option key={bg} value={bg}>{bg}</option>)}
            </select>
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Native (Place/Country) <span style={styles.req}>*</span></label>
            <p style={styles.helper}>Mention your exact Native Place and Country</p>
            <input style={styles.input} name="nativePlace" required value={formData.nativePlace} onChange={handleChange} placeholder="Short answer text" />
          </div>

          <div style={styles.card}>
            <label style={styles.label}>Upload Recent Photo {formData.gender !== 'Female' && <span style={styles.req}>*</span>}</label>
            <input type="file" accept="image/*" required={formData.gender !== 'Female'} onChange={async (e) => {
              const file = e.target.files[0];
              if (file) {
                const compressed = await compressImage(file);
                setFormData({ ...formData, photo: compressed });
              }
            }} />
          </div>

          <button type="submit" disabled={submitting} style={{...styles.submitBtn, opacity: submitting ? 0.7 : 1}}>
            {submitting ? '⏳ Submitting...' : 'Submit'}
          </button>
          {message && <p style={styles.error}>{message}</p>}
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: { backgroundColor: '#f0ebeb', minHeight: '100vh', padding: '20px 0' },
  container: { maxWidth: '640px', margin: '0 auto' },
  banner: { width: '100%', borderRadius: '8px 8px 0 0', display: 'block' },
  card: {
    backgroundColor: '#fff', borderLeft: '4px solid #4285f4', borderRadius: '8px',
    padding: '24px', marginTop: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  successCard: {
    backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px',
    padding: '30px', marginTop: '12px', textAlign: 'center'
  },
  title: { fontSize: '28px', fontWeight: 'normal', margin: '0 0 8px 0' },
  hr: { border: 'none', borderTop: '1px solid #ddd', margin: '12px 0' },
  desc: { fontSize: '14px', margin: '0 0 12px 0', color: '#333' },
  rules: { fontSize: '14px', margin: 0, paddingLeft: '20px', lineHeight: '2' },
  label: { fontSize: '16px', fontWeight: 'bold', display: 'block', marginBottom: '12px' },
  req: { color: '#d93025' },
  input: {
    width: '100%', border: 'none', borderBottom: '1px solid #ddd', padding: '8px 0',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  radio: { display: 'block', padding: '8px 0', fontSize: '14px', cursor: 'pointer' },
  helper: { fontSize: '12px', color: '#666', margin: '0 0 12px 0' },
  submitBtn: {
    backgroundColor: '#7b1fa2', color: '#fff', border: 'none', borderRadius: '4px',
    padding: '10px 24px', fontSize: '14px', cursor: 'pointer', marginTop: '16px'
  },
  error: { color: '#d93025', marginTop: '12px', fontSize: '14px' }
};

export default MembershipPage;
